/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*
* In-memory data for the field lab. Nothing here touches the backend, so the lab keeps working
* whatever happens to the seed data, and experimenting in it can never write to a real database.
*
* Filtering is exact-match on every query param, which is enough to exercise a dependent picker
* (contacts of a unit). Tenancy is not modelled - the lab does not exercise cross-team reads.
*/
import { HttpClient } from '@angular/common/http';
import { HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';
import { AbstractObject, uuid } from '@src/app/model/abstract-object';
import { Unit } from '@src/app/model/unit';
import { AbstractHttpService } from '@src/app/shared/abstract-http.service';
import { MessageService } from '@src/app/shared/message.service';

/** Enough to land on a later tick like an XHR. Raise it to watch loading states */
const LAB_LATENCY_MS = 0;

export abstract class LabService extends AbstractHttpService {
    protected http = inject(HttpClient);
    protected messageService = inject(MessageService);
    protected cache: AbstractObject[] = [];
    protected typeString = 'Lab';

    protected readonly baseUrl = 'lab';

    protected abstract rows: AbstractObject[];

    get<T extends AbstractObject>(_useCache = false, params: HttpParams = null): Observable<T[]> {
        const rows = params ? this.rows.filter(row => this.matches(row, params)) : this.rows;
        return this.respond(rows.slice() as T[]);
    }

    getOne<T>(id: uuid, badReturns: T): Observable<T> {
        return this.respond((this.rows.find(row => row.id === id) ?? badReturns) as T);
    }

    /** Saving updates the in-memory row, so the lab behaves like a real page but writes nothing */
    put<T extends AbstractObject>(object: T): Observable<T> {
        const row = this.rows.find(o => o.id === object.id);
        if (row) {
            Object.assign(row, object);
        }
        return this.respond((row ?? object) as T);
    }

    /**
     * Answer on a later tick, the way an XHR would. Returning of() directly resolves during change
     * detection, which trips ExpressionChangedAfterItHasBeenCheckedError - and worse, would let a
     * component under test pass here while failing against a real service.
     * Raise LAB_LATENCY_MS if you want to watch loading states.
     */
    private respond<T>(payload: T): Observable<T> {
        return of(payload).pipe(delay(LAB_LATENCY_MS));
    }

    private matches(row: AbstractObject, params: HttpParams): boolean {
        return params.keys().every(key => {
            if (key === '_forceTeam') {
                return true;
            }
            return String(row[key] ?? '') === params.get(key);
        });
    }
}

export const LAB_UNITS: AbstractObject[] = [
    { id: 'lab-unit-1', name: 'Apartment 1', address: 'Block A, Sunnyside' },
    { id: 'lab-unit-2', name: 'Apartment 2', address: 'Block A, Sunnyside' },
    { id: 'lab-unit-3', name: 'Apartment 3', address: 'Block B, Sunnyside' },
    { id: 'lab-unit-4', name: 'Duplex 4', address: 'Block B, Sunnyside' },
] as unknown as AbstractObject[];

export const LAB_CONTACTS: AbstractObject[] = [
    { id: 'lab-person-1', unitId: 'lab-unit-1', firstName: 'Aoife', lastName: 'Byrne',
        fullName: 'Aoife Byrne', phone: '086 1111111', email: 'aoife@example.com' },
    { id: 'lab-person-2', unitId: 'lab-unit-1', firstName: 'Cormac', lastName: 'Daly',
        fullName: 'Cormac Daly', phone: '086 2222222', email: 'cormac@example.com' },
    { id: 'lab-person-3', unitId: 'lab-unit-2', firstName: 'Eimear', lastName: 'Fallon',
        fullName: 'Eimear Fallon', phone: '086 3333333', email: 'eimear@example.com' },
    { id: 'lab-person-4', unitId: 'lab-unit-3', firstName: 'Gráinne', lastName: 'Hayes',
        fullName: 'Gráinne Hayes', phone: '086 4444444', email: 'grainne@example.com' },
    { id: 'lab-person-5', unitId: 'lab-unit-4', firstName: 'Niall', lastName: 'O\'Connor',
        fullName: 'Niall O\'Connor', phone: '086 5555555', email: 'niall@example.com' },
] as unknown as AbstractObject[];

/** Stands in for a page's model, so filters can be typed the way a real page types them */
export interface LabItem extends AbstractObject {
    unitId: uuid;
    unitName: string;
    /** The nested unit the API returns alongside the id, so the label can be composed client side */
    unit: Unit;
    personId: uuid;
    personName: string;
    title: string;
    startDate: string;
    /** DateTimeField holds epoch seconds, not a date string - 0 is its empty value */
    lastSeenAt: number;
    email: string;
    quantity: number;
    amount: number;
    rate: number;
    notes: string;
    details: string;
    active: boolean;
    phone: string;
    website: string;
    timeSpent: number;
    statusId: number;
    categoryId: number;
    priorityId: number;
    /* Column 5 - the condensed, label on the left, value on the right style */
    ownerId: number;
    reviewDate: string;
    approvedAt: number;
    balance: number;
    channelName: string;
    channelBadge: string;
    workOrder: string;
    entries: LabEntry[];
}

export interface LabEntry extends AbstractObject {
    title: string;
    entryDate: string;
    quantity: number;
    statusId: number;
    done: boolean;
}

/** Enough rows to push a grid well past GridControl.rowsPerPage, so its pager can be seen */
function makeLabEntries(count: number): LabEntry[] {
    const entries: LabEntry[] = [];
    for (let entryNumber = 1; entryNumber <= count; entryNumber++) {
        entries.push({
            id: `lab-entry-${entryNumber}`,
            title: `Dummy entry ${entryNumber}`,
            entryDate: `2026-${String(entryNumber % 12 + 1).padStart(2, '0')}-${String(entryNumber % 28 + 1).padStart(2, '0')}`,
            quantity: entryNumber,
            statusId: entryNumber % 3 + 1,
            done: entryNumber % 2 === 0,
        } as LabEntry);
    }
    return entries;
}

export const LAB_STATUSES: AbstractObject[] = [
    { id: 1, name: 'Open' }, { id: 2, name: 'Waiting' }, { id: 3, name: 'Closed' },
] as unknown as AbstractObject[];

export const LAB_CATEGORIES: AbstractObject[] = [
    { id: 1, name: 'Lighting' }, { id: 2, name: 'Plumbing' },
    { id: 3, name: 'Grounds' }, { id: 4, name: 'Security' },
] as unknown as AbstractObject[];

export const LAB_PRIORITIES: AbstractObject[] = [
    { id: 1, name: 'Low' }, { id: 2, name: 'Normal' }, { id: 3, name: 'Urgent' },
] as unknown as AbstractObject[];

export const LAB_OWNERS: AbstractObject[] = [
    { id: 1, name: 'Aoife Byrne' }, { id: 2, name: 'Cormac Daly' },
] as unknown as AbstractObject[];

/**
 * The records a page would be editing. These carry the flat joined columns the server is moving
 * to - unitId alongside unitName, personId alongside personName - rather than nested child objects.
 */
export const LAB_ITEMS: LabItem[] = [
    {
        id: 'lab-item-1', revision: 1,
        unitId: 'lab-unit-1', unitName: 'Apartment 1',
        unit: { id: 'lab-unit-1', name: 'Apartment 1', address: 'Block A, Sunnyside' },
        personId: 'lab-person-2', personName: 'Cormac Daly',
        title: 'Communal lighting replacement',
        startDate: '2026-03-14',
        lastSeenAt: 1773480600, // 2026-03-14 09:30:00 UTC
        email: 'cormac@example.com',
        quantity: 12,
        amount: 1450.75,
        rate: 0.135,
        notes: 'Contractor attended and replaced four fittings in the lobby.\nTwo remain on order.',
        details: '<p>Lobby fittings <strong>replaced</strong>.</p><ul><li>Four done</li><li>Two on order</li></ul>',
        active: true,
        phone: '086 2222222',
        website: 'https://example.com',
        timeSpent: 150,
        statusId: 1,
        categoryId: 1,
        priorityId: 2,
        ownerId: 1,
        reviewDate: '2026-09-01',
        approvedAt: 1773593100, // 2026-03-15 16:45:00 UTC
        balance: 320.5,
        channelName: 'Maintenance',
        channelBadge: 'teal',
        workOrder: 'Edit Work Order Information',
        entries: makeLabEntries(250),
    },
    {
        id: 'lab-item-2', revision: 1,
        unitId: null, unitName: null, unit: null,
        personId: null, personName: null,
        title: null, startDate: null, lastSeenAt: 0, email: null,
        quantity: null, amount: null, rate: null, notes: null, details: null,
        active: false, phone: null, website: null, timeSpent: null,
        statusId: null, categoryId: null, priorityId: null,
        ownerId: null, reviewDate: null, approvedAt: 0, balance: null,
        channelName: null, channelBadge: null, workOrder: null,
        entries: [],
    },
] as unknown as LabItem[];

@Injectable({ providedIn: 'root' })
export class LabUnitService extends LabService {
    protected rows = LAB_UNITS;
}

@Injectable({ providedIn: 'root' })
export class LabContactService extends LabService {
    protected rows = LAB_CONTACTS;
}

@Injectable({ providedIn: 'root' })
export class LabItemService extends LabService {
    protected rows = LAB_ITEMS;
}
